import { db, getCollectionPath } from '../lib/firebase';
import { collection, query, where, getDocs, doc, setDoc, orderBy, documentId } from 'firebase/firestore';
import { PlayerStats, Match, Team, Player } from '../types';

export class StatsService {
  /**
   * Recalcula las estadísticas de un jugador tomando todos sus partidos completos
   * y guarda el resultado en la colección playerStats.
   */
  static async recalculatePlayerStats(playerId: string, forcedMatch?: Match): Promise<void> {
    try {
      // 1. Obtener todos los partidos del jugador (torneos)
      const matchesQ = query(
        collection(db, getCollectionPath('matches')),
        where('participantIds', 'array-contains', playerId)
      );
      const matchesSnap = await getDocs(matchesQ);
      const tournamentMatches = matchesSnap.docs
        .map(d => ({ id: d.id, ...d.data() } as Match))
        .filter(m => m.status === 'completed');

      if (forcedMatch && forcedMatch.status === 'completed' && !(forcedMatch as any).matchType) {
        const idx = tournamentMatches.findIndex(m => m.id === forcedMatch.id);
        if (idx !== -1) {
          tournamentMatches[idx] = forcedMatch;
        } else {
          tournamentMatches.push(forcedMatch);
        }
      }

      // 1b. Obtener partidos oficiales de matchmaking (public_matches)
      const pubQ = query(
        collection(db, getCollectionPath('public_matches')),
        where('players', 'array-contains', playerId)
      );
      const pubSnap = await getDocs(pubQ);
      const publicMatchesRaw = pubSnap.docs
        .map(d => ({ id: d.id, ...d.data() } as any))
        .filter(m => (m.status === 'completed' || m.status === 'finished') && m.mode === 'oficial');
      
      console.log(`[StatsService] Player ${playerId} has ${publicMatchesRaw.length} OFICIAL matchmaking matches.`);

      // 2. Obtener los equipos relevantes
      const teamIds = new Set<string>();
      tournamentMatches.forEach(m => {
        if (m.team1Id) teamIds.add(m.team1Id);
        if (m.team2Id) teamIds.add(m.team2Id);
      });
      const teamsMap = new Map<string, Team>();
      if (teamIds.size > 0) {
        const missing = Array.from(teamIds);
        const batches: string[][] = [];
        for (let i = 0; i < missing.length; i += 30) {
          batches.push(missing.slice(i, i + 30));
        }
        for (const batch of batches) {
          const tSnap = await getDocs(query(
            collection(db, getCollectionPath('teams')),
            where(documentId(), 'in', batch)
          ));
          tSnap.docs.forEach(td => teamsMap.set(td.id, { id: td.id, ...td.data() } as Team));
        }
      }

      // 2b. Map public_matches to Match objects and add fake teams
      const mappedPubMatches = publicMatchesRaw.map(pm => {
        const res = pm.result || pm.reportedResult;
        if (!res) {
          console.log(`[StatsService] Match ${pm.id} dropped because it has no result.`);
          return null;
        }
        
        let p1='', p2='', p3='', p4='';
        if (pm.pair1 && pm.pair1.length >= 2) {
          p1 = pm.pair1[0] || ''; p2 = pm.pair1[1] || '';
          p3 = pm.pair2?.[0] || ''; p4 = pm.pair2?.[1] || '';
        } else if (pm.slotMapping) {
          p1 = pm.slotMapping[0]||''; p2 = pm.slotMapping[1]||'';
          p3 = pm.slotMapping[2]||''; p4 = pm.slotMapping[3]||'';
        } else {
          p1 = pm.players[0]||''; p2 = pm.players[1]||'';
          p3 = pm.players[2]||''; p4 = pm.players[3]||'';
        }

        const t1Id = `pub_${pm.id}_t1`;
        const t2Id = `pub_${pm.id}_t2`;

        teamsMap.set(t1Id, { id: t1Id, name: 'Pareja 1', player1Id: p1, player2Id: p2, organizerId: '' } as Team);
        teamsMap.set(t2Id, { id: t2Id, name: 'Pareja 2', player1Id: p3, player2Id: p4, organizerId: '' } as Team);

        // Determine winner
        let wId = '';
        if (pm.result && pm.result.winners) {
          // Check if p1 or p2 are in winners
          if (pm.result.winners.includes(p1) || pm.result.winners.includes(p2)) wId = t1Id;
          else wId = t2Id;
        } else if (pm.reportedResult && pm.reportedResult.winnerId) {
          wId = pm.reportedResult.winnerId === 'pair1' ? t1Id : t2Id;
        }

        // Extract score sets
        let s1='', s2='', s3='';
        if (pm.result && pm.result.score) {
          const parts = pm.result.score.split(' ');
          s1 = parts[0] || ''; s2 = parts[1] || ''; s3 = parts[2] || '';
        } else if (pm.reportedResult) {
          s1 = pm.reportedResult.s1 || ''; s2 = pm.reportedResult.s2 || ''; s3 = pm.reportedResult.s3 || '';
        }

        return {
          id: pm.id,
          team1Id: t1Id,
          team2Id: t2Id,
          s1, s2, s3,
          winnerId: wId,
          createdAt: pm.result?.validatedAt || pm.reportedResult?.timestamp || pm.createdAt,
          status: 'completed',
          participantIds: pm.players
        } as Match;
      }).filter(Boolean) as Match[];

      const matches = [...tournamentMatches, ...mappedPubMatches].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      console.log(`[StatsService] Player ${playerId} combined matches to process: ${matches.length}`);

      // 3. Variables de estado
      let totalGamesWon = 0;
      let totalGamesLost = 0;
      let totalSetsPlayed = 0;
      let totalGamesInSets = 0;
      let maxWinStreak = 0;
      let currentWinStreak = 0;
      let cumulativeWins = 0;
      let tieBreaksPlayed = 0;
      let tieBreaksWon = 0;
      let remontadasAttempted = 0;
      let remontadasWon = 0;
      
      const partnerHistory: Record<string, { name: string, photo?: string, played: number, won: number }> = {};
      const winRateHistory: { date: number, rate: number }[] = [];
      const recentForm: ('W' | 'L')[] = [];

      // Set to hold unique partner IDs to fetch their names
      const partnerIdsToFetch = new Set<string>();

      matches.forEach((m: any, idx) => {
        const t1 = m.team1Obj || teamsMap.get(m.team1Id);
        const t2 = m.team2Obj || teamsMap.get(m.team2Id);
        
        const isT1 = t1?.player1Id === playerId || t1?.player2Id === playerId || t1?.player3Id === playerId;
        const myTeamId = isT1 ? m.team1Id : m.team2Id;
        const myTeam = isT1 ? t1 : t2;
        const won = m.winnerId === myTeamId;

        if (won) {
          currentWinStreak++;
          if (currentWinStreak > maxWinStreak) maxWinStreak = currentWinStreak;
          cumulativeWins++;
        } else {
          currentWinStreak = 0;
        }

        recentForm.push(won ? 'W' : 'L');
        winRateHistory.push({ date: m.createdAt || Date.now(), rate: (cumulativeWins / (idx + 1)) * 100 });

        const setsData = [m.s1, m.s2, m.s3].filter(Boolean).map(s => {
          const parts = s!.split('-').map(str => parseInt(str.trim(), 10));
          return { p1: parts[0] || 0, p2: parts[1] || 0 };
        });

        if (setsData.length > 0) {
          const s1 = setsData[0];
          const lostFirstSet = isT1 ? s1.p1 < s1.p2 : s1.p2 < s1.p1;
          if (lostFirstSet) {
            remontadasAttempted++;
            if (won) remontadasWon++;
          }

          setsData.forEach(set => {
            totalSetsPlayed++;
            totalGamesInSets += (set.p1 + set.p2);

            const isTieBreak = (set.p1 === 7 && set.p2 === 6) || (set.p1 === 6 && set.p2 === 7);
            if (isTieBreak) {
              tieBreaksPlayed++;
              const iWonTieBreak = isT1 ? set.p1 === 7 : set.p2 === 7;
              if (iWonTieBreak) tieBreaksWon++;
            }

            if (isT1) {
              totalGamesWon += set.p1;
              totalGamesLost += set.p2;
            } else {
              totalGamesWon += set.p2;
              totalGamesLost += set.p1;
            }
          });
        }

        if (myTeam) {
          const partnerId = [myTeam.player1Id, myTeam.player2Id, myTeam.player3Id].find(id => id && id !== playerId);
          if (partnerId) {
            if (!partnerHistory[partnerId]) {
              partnerHistory[partnerId] = { name: 'Compañero', played: 0, won: 0 };
              partnerIdsToFetch.add(partnerId);
            }
            partnerHistory[partnerId].played++;
            if (won) partnerHistory[partnerId].won++;
          }
        }
      });

      // 4. Fetch missing partner details
      if (partnerIdsToFetch.size > 0) {
        const missingIds = Array.from(partnerIdsToFetch);
        const pBatches: string[][] = [];
        for (let i = 0; i < missingIds.length; i += 30) {
          pBatches.push(missingIds.slice(i, i + 30));
        }
        for (const batch of pBatches) {
          const pSnap = await getDocs(query(
            collection(db, getCollectionPath('players')),
            where(documentId(), 'in', batch)
          ));
          pSnap.docs.forEach(docSnap => {
            const data = docSnap.data() as Player;
            if (partnerHistory[docSnap.id]) {
              partnerHistory[docSnap.id].name = data.name;
              partnerHistory[docSnap.id].photo = data.photoUrl || '';
            }
          });
        }
      }

      // 5. Construct stats object
      const stats: PlayerStats = {
        playerId,
        totalGamesWon,
        totalGamesLost,
        totalSetsPlayed,
        totalGamesInSets,
        maxWinStreak,
        currentWinStreak,
        cumulativeWins,
        totalCompletedMatches: matches.length,
        tieBreaksPlayed,
        tieBreaksWon,
        remontadasAttempted,
        remontadasWon,
        partnerHistory,
        winRateHistory,
        recentForm: recentForm.slice(-20),
        updatedAt: Date.now()
      };

      // 6. Save to Firestore
      const statsRef = doc(db, getCollectionPath('playerStats'), playerId);
      await setDoc(statsRef, stats);
      console.log(`Stats updated for player ${playerId}`);
      
      const { addDoc } = await import('firebase/firestore');
      await addDoc(collection(db, 'debug_logs'), { type: 'stats_success', playerId, matchesCount: matches.length, streak: stats.currentWinStreak, time: Date.now() });
    } catch (error: any) {
      console.error('Error recalculating player stats:', error);
      const { addDoc } = await import('firebase/firestore');
      await addDoc(collection(db, 'debug_logs'), { type: 'stats_error', playerId, error: error?.message || String(error), time: Date.now() });
    }
  }

  static async processMatchStats(match: Match | any): Promise<void> {
    console.log("[StatsService] processMatchStats called with:", match);
    try {
      const pIds = match.participantIds || match.players;
      console.log("[StatsService] processMatchStats pIds:", pIds);
      if (!pIds || pIds.length === 0) {
        console.log("[StatsService] processMatchStats aborted due to empty pIds");
        return;
      }
      
      const promises = pIds.map((pid: string) => {
        console.log("[StatsService] scheduling recalculate for", pid);
        return this.recalculatePlayerStats(pid, match);
      });
      await Promise.all(promises);
    } catch (err) {
      console.error("Error processing match stats:", err);
    }
  }

}
